import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPAButton from '../shared/SPAButton';
import SPADataTable from '../shared/SPADataTable';
import { Limits } from './types';

interface LimitRow extends Limits {
  identity: string;
}

interface UserLimitsTableProps {
  isSaving: boolean;
  onClear: (identity: string) => void;
  userLimits: Record<string, Limits>;
}

const columnHelper = createColumnHelper<LimitRow>();

// "Default" means that gate is still on the default — a user limit may set one gate without the other.
const limitCell = (value?: number) => (
  value == null ? <span className="tw-text-spa-tertiary">Default</span> : value.toLocaleString()
);

const UserLimitsTable = ({ isSaving, onClear, userLimits }: UserLimitsTableProps) => {
  const rows = Object.entries(userLimits).map(([identity, limits]) => ({ identity, ...limits }));

  const columns = [
    columnHelper.accessor('identity', {
      cell: info => <span className="tw-font-mono tw-text-xs">{info.getValue()}</span>,
      header: 'Identity',
    }),
    columnHelper.accessor('daily_limit_usd', {
      cell: info => limitCell(info.getValue()),
      header: '$ / day',
      meta: { align: 'right' },
    }),
    columnHelper.accessor('requests_per_minute', {
      cell: info => limitCell(info.getValue()),
      header: 'Req / min',
      meta: { align: 'right' },
    }),
    columnHelper.display({
      cell: info => (
        <SPAButton
          disabled={isSaving}
          size="xs"
          variant="danger-outline"
          onClick={() => onClear(info.row.original.identity)}
        >
          Clear
        </SPAButton>
      ),
      header: () => <span className="tw-sr-only">Actions</span>,
      id: 'actions',
      meta: { align: 'right', width: 1 },
    }),
  ] as ColumnDef<LimitRow, unknown>[];

  return (
    <SPADataTable
      columns={columns}
      data={rows}
      defaultSort={[]}
      emptyMessage="Everyone is on the defaults."
    />
  );
};

export default UserLimitsTable;
